import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import TranslatableField from "./TranslatableField";
import Toggle from "./Toggle";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { focusFirstError } from "../services/formFocus";
import { createProvider, fetchProvider, updateProvider } from "../services/providerService";
import { fetchUsers } from "../services/userService";
import { can } from "../services/permissions";
import { getCurrentUser } from "../services/authService";
import { labelFor } from "../services/enumLabels";

const EMPLOYMENT_STATUSES = ["active", "leave", "inactive"];
const COMMISSION_TYPES = ["none", "percentage", "fixed"];

const blank = {
  displayName: { en: "", ar: "" },
  jobTitle: { en: "", ar: "" },
  bio: { en: "", ar: "" },
  userId: "",
  employmentStatus: "active",
  isBookableOnline: true,
  commissionType: "none",
  commissionValue: "",
  sortOrder: 0,
  branchIds: [],
  serviceIds: [],
};

/** An existing provider -> the form's shape, both locales prefilled. */
const toForm = (provider) => ({
  displayName: {
    en: provider.translations?.display_name?.en ?? "",
    ar: provider.translations?.display_name?.ar ?? "",
  },
  jobTitle: {
    en: provider.translations?.job_title?.en ?? "",
    ar: provider.translations?.job_title?.ar ?? "",
  },
  bio: {
    en: provider.translations?.bio?.en ?? "",
    ar: provider.translations?.bio?.ar ?? "",
  },
  userId: provider.user_id ?? "",
  employmentStatus: provider.employment_status ?? "active",
  isBookableOnline: Boolean(provider.is_bookable_online),
  // Absent, not null, when the caller may not see it — so "none" is only ever
  // shown to somebody who is actually allowed to read the real value.
  commissionType: provider.commission_type ?? "none",
  commissionValue: provider.commission_value ?? "",
  sortOrder: provider.sort_order ?? 0,
  branchIds: (provider.branches ?? []).map((b) => b.id),
  serviceIds: (provider.services ?? []).map((s) => s.id),
});

/**
 * BIZ-08 — a provider's profile. Who they are, where they work, what they may
 * perform, and (for a caller holding staff.manage_commission) what they are
 * paid.
 *
 * The commission block is hidden without that permission. That is a convenience
 * only: the API drops the fields regardless of what the form sends.
 */
export default function ProviderModal({ provider, branches, services, open, onClose, onSaved }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(blank);
  /** The provider whose record the form currently holds; null while one is read. */
  const [hydratedId, setHydratedId] = useState(null);
  const [logins, setLogins] = useState([]);
  /**
   * OUP-27 — a provider stored without a portal login predates the rule that
   * made one compulsory, and the API exempts exactly those rows so an unrelated
   * edit is not blocked behind a question the record was never asked. Read from
   * the provider as loaded, not from the form: choosing a login and clearing it
   * again within the same edit must not keep the exemption alive.
   */
  const loginExempt = Boolean(provider) && !provider.user_id;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const user = getCurrentUser();
  const mayManageCommission = can(user, "staff.manage_commission");

  useEffect(() => {
    if (!open) return;

    setError("");
    setFieldErrors({});

    if (!provider) {
      setForm(blank);
      setHydratedId(null);
      return;
    }

    /*
     * OUP-24's lesson, on the provider's side of the same pivot. The list
     * screen hands over the row it already has, and that row carries
     * `services_count` but not the services themselves — /business/staff
     * eager-loads branches and not `service_staff`. Filling the form from it
     * opened the eligibility picker with nothing ticked on a provider who
     * performs plenty. The record is read again from /business/staff/{id}, which
     * does load them, so the form fills the same way from either entry point.
     */
    let cancelled = false;

    setHydratedId(null);

    fetchProvider(provider.id)
      .then((full) => {
        if (cancelled) return;

        setForm(toForm(full));
        setHydratedId(full.id);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, t("providers.loadOneFailed")));
      });

    return () => {
      cancelled = true;
    };
    // The record is keyed by id, not by the object the caller happens to hold,
    // and t is read but never worth re-reading the provider for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider?.id]);

  // The portal login this provider works under — required, so the list must
  // load before the form can be saved.
  useEffect(() => {
    if (!open) return;

    fetchUsers({ perPage: 100 })
      .then(({ users }) => setLogins(users))
      .catch(() => setLogins([]));
  }, [open]);

  if (!open) return null;

  /*
   * The form waits for that read instead of drawing itself from the row the
   * list had: an empty picker is the bug, and a tick made before the record
   * landed would be overwritten when it did. A read that failed says so rather
   * than offering an empty picker that a save would then act on.
   */
  if (provider && hydratedId !== provider.id) {
    return (
      <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
        <div className="card my-8 w-full max-w-2xl p-5">
          <h2 className="mb-4 text-lg font-bold">{t("providers.editTitle")}</h2>
          {error ? (
            <p className="field-error">{error}</p>
          ) : (
            <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const update = (changes) => setForm((current) => ({ ...current, ...changes }));

  const toggleIn = (key, id) =>
    update({
      [key]: form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id],
    });

  /** OUP-23 — API error key -> the id of the control that owns it, in form order. */
  const FIELD_IDS = {
    display_name: "display_name-en",
    job_title: "job_title-en",
    bio: "bio-en",
    branches: "provider-branches",
    services: "provider-services",
    employment_status: "provider-status",
    user_id: "provider-login",
    is_bookable_online: "provider-bookable-online",
    commission_type: "provider-commission-type",
    commission_value: "provider-commission-value",
  };

  const report = (found) => {
    setFieldErrors(found);
    focusFirstError(found, FIELD_IDS);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    /*
     * OUP-23 — a provider needs somewhere to work and something to do. Without
     * a branch they appear in no diary; without a service the availability
     * engine can never offer them. The API refuses both on create, so say it
     * here rather than spending a round trip to be told.
     *
     * The portal login is required on create and on edit alike — every provider
     * is issued one — so it is checked here on the same terms. The exception is
     * a row that predates the rule; see loginExempt above.
     */
    const found = {};
    if (form.branchIds.length === 0) found.branches = [t("providers.form.errors.branches")];
    if (form.serviceIds.length === 0) found.services = [t("providers.form.errors.services")];
    if (!form.userId && !loginExempt) found.user_id = [t("providers.form.errors.login")];

    if (Object.keys(found).length > 0) {
      report(found);
      setError(t("providers.form.errors.summary"));
      return;
    }

    setSaving(true);
    setFieldErrors({});

    const values = {
      nameEn: form.displayName.en,
      nameAr: form.displayName.ar,
      jobTitleEn: form.jobTitle.en,
      jobTitleAr: form.jobTitle.ar,
      bioEn: form.bio.en,
      bioAr: form.bio.ar,
      userId: form.userId,
      employmentStatus: form.employmentStatus,
      isBookableOnline: form.isBookableOnline,
      sortOrder: form.sortOrder,
      branchIds: form.branchIds,
      serviceIds: form.serviceIds,
      // Left undefined entirely when the caller may not set it, so the payload
      // carries no commission keys at all.
      commissionType: mayManageCommission ? form.commissionType : undefined,
      commissionValue: mayManageCommission ? form.commissionValue : undefined,
    };

    try {
      const saved = provider
        ? await updateProvider(provider.id, values)
        : await createProvider(values);

      onSaved(saved, Boolean(provider));
      onClose();
    } catch (err) {
      report(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("providers.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-2xl p-5">
        <h2 className="mb-4 text-lg font-bold">
          {provider ? t("providers.editTitle") : t("providers.newTitle")}
        </h2>

        <form onSubmit={submit} className="space-y-4">
          <TranslatableField
            name="display_name"
            label={t("providers.form.displayName")}
            value={form.displayName}
            onChange={(displayName) => update({ displayName })}
            errors={fieldErrors}
            required
          />

          <TranslatableField
            name="job_title"
            label={t("providers.form.jobTitle")}
            value={form.jobTitle}
            onChange={(jobTitle) => update({ jobTitle })}
            errors={fieldErrors}
          />

          <TranslatableField
            name="bio"
            label={t("providers.form.bio")}
            value={form.bio}
            onChange={(bio) => update({ bio })}
            errors={fieldErrors}
            textarea
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="provider-status">
                {t("providers.form.employmentStatus")}
              </label>
              <select
                id="provider-status"
                className="field-input"
                value={form.employmentStatus}
                onChange={(e) => update({ employmentStatus: e.target.value })}
              >
                {EMPLOYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {labelFor("employment_status", status)}
                  </option>
                ))}
              </select>
              {fieldErrors.employment_status && (
                <p className="field-error">{fieldErrors.employment_status[0]}</p>
              )}
            </div>

            <div>
              <label className="field-label" htmlFor="provider-login">
                {t("providers.form.login")}
                {!loginExempt && <span className="text-danger-text"> *</span>}
              </label>
              <select
                id="provider-login"
                className="field-input"
                value={form.userId}
                onChange={(e) => update({ userId: e.target.value })}
                required={!loginExempt}
                aria-required={!loginExempt}
              >
                <option value="" disabled={!loginExempt}>
                  {t("providers.form.pickLogin")}
                </option>
                {logins.map((login) => (
                  <option key={login.id} value={login.id}>
                    {login.name} — {login.email}
                  </option>
                ))}
              </select>
              {fieldErrors.user_id && <p className="field-error">{fieldErrors.user_id[0]}</p>}
            </div>
          </div>

          <Toggle
            id="provider-bookable-online"
            label={t("providers.form.bookableOnline")}
            hint={t("providers.form.bookableOnlineHint")}
            checked={form.isBookableOnline}
            onChange={(isBookableOnline) => update({ isBookableOnline })}
          />

          {/* Where they work */}
          <div>
            <p className="field-label" id="provider-branches" tabIndex={-1}>
              {t("providers.form.branches")} <span className="text-danger-text">*</span>
            </p>
            {branches.length === 0 ? (
              <p className="text-sm text-ink-2">{t("providers.form.noBranchesYet")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => toggleIn("branchIds", branch.id)}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      form.branchIds.includes(branch.id)
                        ? "border-accent bg-accent-wash text-accent-text"
                        : "border-line text-ink-2"
                    }`}
                  >
                    {branch.name}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-ink-2">{t("providers.form.branchesHint")}</p>
            {fieldErrors.branches?.[0] && <p className="field-error">{fieldErrors.branches[0]}</p>}
          </div>

          {/* What they may perform — file 05's eligibility */}
          <div>
            <p className="field-label" id="provider-services" tabIndex={-1}>
              {t("providers.form.services")} <span className="text-danger-text">*</span>
            </p>
            {services.length === 0 ? (
              <p className="text-sm text-ink-2">{t("providers.form.noServicesYet")}</p>
            ) : (
              <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                {services.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => toggleIn("serviceIds", service.id)}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      form.serviceIds.includes(service.id)
                        ? "border-accent bg-accent-wash text-accent-text"
                        : "border-line text-ink-2"
                    }`}
                  >
                    {service.name}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-ink-2">{t("providers.form.servicesHint")}</p>
            {fieldErrors.services?.[0] && <p className="field-error">{fieldErrors.services[0]}</p>}
          </div>

          {/* Pay — hidden without staff.manage_commission, and dropped by the
              API too, so hiding it is never the control. */}
          {mayManageCommission && (
            <div className="rounded-lg border border-warning-line bg-warning-wash p-3">
              <p className="mb-2 text-sm font-medium text-warning-text">
                {t("providers.form.commission")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="provider-commission-type">
                    {t("providers.form.commissionType")}
                  </label>
                  <select
                    id="provider-commission-type"
                    className="field-input"
                    value={form.commissionType}
                    onChange={(e) => update({ commissionType: e.target.value })}
                  >
                    {COMMISSION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {labelFor("commission_type", type)}
                      </option>
                    ))}
                  </select>
                </div>

                {form.commissionType !== "none" && (
                  <div>
                    <label className="field-label" htmlFor="provider-commission-value">
                      {form.commissionType === "percentage"
                        ? t("providers.form.commissionPercent")
                        : t("providers.form.commissionAmount")}
                    </label>
                    <input
                      id="provider-commission-value"
                      type="number"
                      step="0.01"
                      min="0"
                      className="field-input"
                      value={form.commissionValue}
                      onChange={(e) => update({ commissionValue: e.target.value })}
                    />
                    {fieldErrors.commission_value && (
                      <p className="field-error">{fieldErrors.commission_value[0]}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {error && <p className="field-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
